// @ts-nocheck
// ====== Error tracking centralizzato (Sentry) ======
// Sentry SDK loaded via CDN solo se VITE_SENTRY_DSN e' configurato in .env (Pacchetto S7).
// Tutti gli errori dell'app passano da handleError() invece di console.error,
// cosi' abbiamo visibilita' centralizzata su quanto succede in produzione.
//
// Per attivarlo:
//   1. Crea progetto "portale-mase" Browser JavaScript su sentry.io (free tier 5k events/mese)
//   2. Copia la DSN
//   3. Imposta VITE_SENTRY_DSN nelle env del deployment (Cloudflare Pages → Settings → Env vars)
//      oppure in `.env.local` per dev.
// In dev mode (localhost) Sentry NON viene attivato anche se la DSN e' impostata.

const SENTRY_DSN =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SENTRY_DSN) ||
  (typeof globalThis !== 'undefined' && globalThis.SENTRY_DSN) ||
  '';
const SENTRY_RELEASE =
  (typeof import.meta !== 'undefined' &&
    (import.meta.env?.VITE_APP_VERSION || import.meta.env?.npm_package_version)) ||
  'portale-mase@dev';
const DEV_MODE = location.hostname === 'localhost' || location.hostname === '127.0.0.1';

let _sentryReady = false;

async function initSentry() {
  if (!SENTRY_DSN || DEV_MODE) {
    console.info('[error-tracking] Sentry NOT initialised (DSN missing or dev mode)');
    return;
  }
  try {
    // Lazy import via CDN per non gonfiare il bundle
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://browser.sentry-cdn.com/8.0.0/bundle.min.js';
      s.crossOrigin = 'anonymous';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    if (!window.Sentry) throw new Error('Sentry SDK not available after load');
    window.Sentry.init({
      dsn: SENTRY_DSN,
      tracesSampleRate: 0.1,
      release: SENTRY_RELEASE,
      environment: location.hostname.includes('pages.dev') ? 'production' : 'staging',
      beforeSend(event) {
        // Filtra rumore di estensioni browser
        if (event.exception?.values?.[0]?.value?.includes('keepassxc-browser')) return null;
        if (event.exception?.values?.[0]?.value?.includes('chrome-extension://')) return null;
        return event;
      }
    });
    // Tag utente loggato (best effort)
    const u = typeof getCurrentUser === 'function' ? getCurrentUser() : null;
    if (u) window.Sentry.setUser({ email: u.email, username: u.name, role: u.role });
    _sentryReady = true;
    console.info('[error-tracking] Sentry ready');
  } catch (e) {
    console.warn('[error-tracking] Sentry init failed:', e);
  }
}

/**
 * Wrapper centralizzato per gestione errori. Usato dal codice business
 * invece di `console.error + toast(...)` sparsi.
 * @param {unknown} err
 * @param {string} context Descrizione contesto (es. 'saveClient', 'login')
 * @param {Object} [extras] Dati aggiuntivi da loggare
 */
function handleError(err, context, extras) {
  const msg = err?.message || String(err);
  const userMsg = extras?.userMessage || `Errore: ${msg}`;

  // Console sempre (anche in prod, l'utente puo' aprire DevTools)
  console.error(`[${context}]`, err, extras);

  // Sentry se attivo
  if (_sentryReady && window.Sentry) {
    window.Sentry.withScope((scope) => {
      scope.setTag('context', context);
      if (extras) {
        Object.entries(extras).forEach(([k, v]) => {
          if (k !== 'userMessage') scope.setExtra(k, v);
        });
      }
      window.Sentry.captureException(err instanceof Error ? err : new Error(msg));
    });
  }

  // Toast all'utente (a meno che extras.silent === true)
  if (!extras?.silent && typeof toast === 'function') {
    toast(userMsg);
  }
}

// Cattura globale: errori non gestiti + promise rejection non gestite
window.addEventListener('error', (e) => {
  if (!_sentryReady) return; // se Sentry non attivo, log solo
  handleError(e.error || new Error(e.message), 'window.error', { silent: true });
});

window.addEventListener('unhandledrejection', (e) => {
  if (!_sentryReady) return;
  handleError(e.reason || new Error('unhandledrejection'), 'unhandledrejection', { silent: true });
});

// Avvio non-bloccante (Sentry init e' async, niente await su questo)
initSentry();

Object.assign(globalThis, { handleError, initSentry });
export {};
